<template>
  <div v-if="folders.length > 0">
    <ul
      id="project-docs-accordion"
      class="accordion"
      data-accordion
      data-deep-link="true"
      data-allow-all-closed="true"
      data-multi-expand="true"
      data-update-history="true"
      data-deep-link-smudge="500"
    >
      <li
        v-for="(folder, index) in folders"
        :key="index"
        class="accordion-item"
        data-accordion-item
      >
        <a
          :href="'#project-docs-accordion-' + index"
          class="accordion-title"
        >
          <div class="row">
            <div class="medium-6 column title-label">
              <div class="title-index">{{ index + 1 }}</div><span class="folder-title">{{ folder.folderType }}</span>
              <div
                v-if="isPicView && !isAppr(folder)"
                class="project-docs-accordion-menu"
              >
                <dotted-dropdown :id="`project-docs-dotted-dropdown-${index}`">
                  <li>
                    <a
                      :id="`project-docs-dotted-dropdown-delete-${index}`"
                      @click="() => deleteFolder(folder.id)"
                    >
                      {{ $t('action.delete') }}
                    </a>
                  </li>
                </dotted-dropdown>
              </div>
            </div>
            <div
              v-if="!isDraft(folder)"
              class="medium-5 column uppercase-bold doc-status"
            >
              <div
                class="doc-status-rect"
                :class="{'rejected-status': !isAppr(folder), 'accepted-status': isAppr(folder)}"
              >
                {{ isAppr(folder) ? $t('accepted') : $t('rejected') }}
              </div>
            </div>
          </div>
        </a>
        <div class="project-docs-accordion-info">
          <div class="project-docs-accordion-desc">
            <textarea
              v-if="allowChangeName[index]"
              :id="`project-docs-accordion-doctype-desc-${index}`"
              v-model="folder.description"
              v-autosize
              type="text"
              class="autosize"
              maxlength="255"
              autofocus
              @focusout="() => updateDescription(folder, index)"
            />
            <div v-else>
              <pre @dblclick="() => dblClickEdit(index, folder)"><span class="emphasis">{{ folder.description || (isPicViewLike && !isAppr(folder) ? $t('project.docs.fillDescHint') : '') }}</span><!--
                  Comment for removing new line inside element
                --><a
                  v-if="isPicViewLike && !isAppr(folder)"
                  id="settings-engineers-security-group-detail-name-edit"
                  class="fa fa-pencil icon"
                  @click="() => Vue.set(allowChangeName, index, true)"
                />
              </pre>
            </div>
          </div>
        </div>
        <div
          :id="'project-docs-accordion-' + index"
          class="accordion-content"
          data-tab-content
        >
          <HkldDropzone
            v-if="isOnlyContractor && !isAppr(folder) && isProjectActive"
            :sourceId="folder.id"
            :action="getCreateDocAction()"
            :metaData="{uploadOnTop: true}"
          />
          <EditAttachments
            :sourceId="folder.id"
            :canEdit="isOnlyContractor && !isAppr(folder)"
            :canDelete="isOnlyContractor || isPicViewLike"
            :canDownloadAll="isPicViewLike"
            :showAllDeleteBtn="false"
            :deleteFileCb="deleteFile"
            :uploadOnTop="true"
            :showDeleteBtnFunc="showDeleteBtnFunc(folder)"
            :zipName="`${currentProject.number}_Vendor_documents.zip`"
            :action="getDeleteDocAction()"
          >
            <div
              v-if="isPicView && !isAppr(folder)"
              slot="buttons"
              class="float-right clearfix"
            >
              <button
                type="button"
                class="btn btn--darkblue tender-sticky-button"
                @click="() => rejectFolder(folder.id)"
              >
                {{ $t('action.reject') }}
              </button>
              <button
                type="button"
                class="btn btn--orange tender-sticky-button"
                @click="() => approveFolder(folder.id)"
              >
                {{ $t('action.accept') }} &amp; {{ $t('action.lock') }}</button>
            </div>
          </EditAttachments>
          <div
            v-show="folder.rejectHistory && folder.rejectHistory.length > 0"
            class="clearfix action-history"
          >
            <div>
              <label>{{ $t('history') }}</label>
            </div>
            <reject-history
              v-for="(history, i) in folder.rejectHistory"
              :key="i"
              :item="history"
              :isContractor="isContractor"
              :showRejectHeader="!i"
              :class="{'odd-row': i%2 }"
            />
          </div>
          <HKLViewLayout
            v-if="folder.docs.length && !isAppr(folder)"
            :checkIsRepresentative="true"
          >
            <ProjectRepRemarks
              @review="(...args) => review(folder, ...args)"
            />
          </HKLViewLayout>
        </div>
      </li>
    </ul>
    <reject-modal
      :id="rejectModalId"
      ref="rejectModal"
    />
    <approve-folder ref="approveFolder" />
    <delete-folder ref="deleteFolder" />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import lodash from 'lodash'
import Vue from 'vue'
import { getCurrentInstance, unref, toRefs, computed } from 'vue-demi'
import DottedDropdown from '@/controls/DottedDropdown'
import EventBus from '@/utils/eventBus'
import dateUtil from '@/utils/dateUtil'
import { FOLDER_STATUS, ActionTypes, DocTypes } from '@/utils/consts'
import { updateFolder, updateFolderStatus } from '@/api/folders/request'
import DeleteFolder from '@/modules/projects/documents/DeleteFolder'
import ApproveFolder from '@/modules/projects/documents/ApproveFolder'
import RejectHistory from '@/modules/projects/documents/RejectHistory'
import RejectModal from '@/modules/projects/RejectModal'
import HkldDropzone from '@/controls/HkldDropzone'
import EditAttachments from '@/controls/EditAttachments'
import { getRandomIdSuffix } from '@/utils/vueCompHelper'
import { aclHelper } from '@/utils/aclHelper'
import { projectStatus } from '@/utils/locale'
import mutationTypes from '@/store/mutation-types'
import HKLViewLayout from '@/controls/Layout/HKLViewLayout.vue'
import { useStoreActions, useStoreGetters } from '@/store/index'
import { useIsProjectRepresentative, useProjectRepReviewName } from '@/pages/projects/projectInfo'
import ProjectRepRemarks from '../ProjectRepRemarks.vue'
import { PROJECT_FOLDER_STATUS } from '../consts'

export default {
  components: {
    DottedDropdown,
    DeleteFolder,
    ApproveFolder,
    RejectHistory,
    RejectModal,
    HkldDropzone,
    EditAttachments,
    HKLViewLayout,
    ProjectRepRemarks,
  },
  props: {
    isContractor: {
      type: Boolean,
      default: true,
    },
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    let returnData = {}
    const {
      isContractor: isContractorRef,
    } = toRefs(props)
    const isProjectRepresentative = useIsProjectRepresentative()
    const isPicViewLike = computed(() => {
      return proxy.isPicView || unref(isProjectRepresentative)
    })
    const isOnlyContractor = computed(() => {
      return unref(isContractorRef) && !unref(isProjectRepresentative)
    })
    returnData = {
      ...returnData,
      isProjectRepresentative,
      isPicViewLike,
      isOnlyContractor,
    }
    const { currentUser } = useStoreGetters('user', ['currentUser'])
    const { loadProject } = useStoreActions('projects', ['loadProject'])
    const curUserReviewName = useProjectRepReviewName()
    const review = async (folder, remarksContent, { remarks }) => {
      const actionHistory = {
        actionType: PROJECT_FOLDER_STATUS.REVIEW,
        detail: { remarks: remarksContent || '' },
        reviewer: {
          id: unref(currentUser).id,
          name: unref(curUserReviewName),
          username: unref(currentUser).username,
        },
      }
      await updateFolder({ actionHistory }, folder.id)
      remarks.value = ''
      await loadProject(proxy.projectId)
      proxy.refreshPage()
    }
    returnData = {
      ...returnData,
      review,
    }

    const { isHKlandUser } = useStoreGetters('user', ['isHKlandUser'])
    const parseHistory = (historys) => {
      if (historys) {
        return historys.filter(history => history.actionType)
          .map(history => {
            if (history.actionType !== PROJECT_FOLDER_STATUS.REVIEW) return history
            if (unref(isHKlandUser) || unref(isProjectRepresentative)) {
              return lodash.defaultsDeep({
                approver: history.reviewer,
                detail: {
                  rejectReason: history.detail.remarks,
                },
              }, history)
            }
            return void 0
          })
          .filter(Boolean)
      }
      return []
    }
    returnData = {
      ...returnData,
      parseHistory,
    }
    return returnData
  },
  data () {
    return {
      dateUtil,
      isLocked: false,
      folders: [],
      allowChangeName: [],
      Vue,
      rejectModalId: `rejectDocs-${getRandomIdSuffix()}`,
    }
  },
  computed: {
    ...mapGetters('projects', [
      'currentProject',
    ]),
    ...mapGetters('user', ['currentUser']),
    isPicView () {
      return aclHelper.isProjectPic()
    },
    projectId () {
      return this.currentProject.id
    },
    isProjectActive () {
      return this.currentProject.status === projectStatus.ACTIVE
    },
  },
  destroyed () {
    if (this.accordion && Object.keys(this.accordion).length > 0) {
      if ($('#project-docs-accordion').childElementCount > 0) {
        this.accordion.destroy()
      }
      delete this.accordion
    }
  },
  async mounted () {
    EventBus.on(`modules_projects_rejectModal_doReject_${this.rejectModalId}`, async (payload) => {
      this.handleRejectEvent(payload)
    })
    EventBus.on(`modules_projects_rejectModal_doCloseSuccess_${this.rejectModalId}`, async () => {
      await this.refreshPage()
    })
    EventBus.on('modules.projects.documents.folderUpdated', async () => {
      setTimeout(this.refreshPage, 300)
    })
    await this.refreshPage()
  },
  methods: {
    ...mapMutations('projects', {
      updateProjectField: mutationTypes.UPDATE_PATH_FIELD,
    }),
    getCreateDocAction () {
      return ActionTypes.projects.doc.create
    },
    getDeleteDocAction () {
      return ActionTypes.projects.doc.delete
    },
    formatDate (dateString) {
      return dateUtil.formatDate(dateString)
    },
    async deleteFile (item, files) {
      const sourceId = item.source
      if (sourceId) {
        const fileList = lodash.cloneDeep(files)
        const folderIndex = this.folders.findIndex(item => item.id === sourceId)
        folderIndex > -1 && this.updateProjectField({ path: ['folders', folderIndex, 'docs'], newValue: fileList })
      }
    },
    showDeleteBtnFunc (folder) {
      return (item) => {
        const canPicDel = this.isPicViewLike && !this.isAppr(folder)
        if (folder.status === FOLDER_STATUS.REJECTED && this.isContractor) {
          const rejectedHistory = folder.rejectHistory[0]
          return item.createdAt < rejectedHistory.updatedAt
        }
        return canPicDel
      }
    },
    initAccordion () {
      this.accordionStatus = []
      if (this.accordion && Object.keys(this.accordion).length > 0) {
        if ($('#project-docs-accordion').childElementCount > 0) {
          this.accordion.destroy()
        }
        delete this.accordion
      }
      if (!this.accordion) {
        this.accordion = new Foundation.Accordion($('#project-docs-accordion'))
        $('#project-docs-accordion').on('down.zf.accordion', (event, el) => {
          const index = Number(el.attr('id').match(/\d+$/)[0])
          this.accordionStatus[index] = true
          this.$emit('docAccordionExpand', this.accordionStatus.every(status => status))
        })
        $('#project-docs-accordion').on('up.zf.accordion', (event, el) => {
          const index = Number(el.attr('id').match(/\d+$/)[0])
          this.accordionStatus[index] = false
          this.$emit('docAccordionExpand', this.accordionStatus.every(status => status))
        })
      }

      this.folders.forEach((folder, index) => {
        const el = $(`#project-docs-accordion #project-docs-accordion-${index}`)
        if (!this.isAppr(folder)) {
          this.accordion.down(el)
        } else {
          this.accordion.up(el)
        }
      })
    },
    expandAll () {
      this.folders.forEach((folder, index) => {
        const el = $(`#project-docs-accordion #project-docs-accordion-${index}`)
        this.accordion.down(el)
      })
    },
    collapseAll () {
      this.folders.forEach((folder, index) => {
        const el = $(`#project-docs-accordion #project-docs-accordion-${index}`)
        this.accordion.up(el)
      })
    },
    refreshPage () {
      const data = this.currentProject.folders
      if (data) {
        const sortedFolders = Object.entries(lodash.orderBy(data, ['createdAt', (item) => Object.values(DocTypes).indexOf(item.folderType)]))
          .map(([index, folder]) => {
            folder.rejectHistory = this.parseHistory(folder.actionHistory)
            Vue.set(this.allowChangeName, index, false)
            return folder
          })
        this.folders = lodash.cloneDeep(sortedFolders)
      }
      setTimeout(this.initAccordion, 0)
    },
    async updateDescription (folder, index) {
      Vue.set(this.allowChangeName, index, false) // eslint-disable-line
      if (!folder.description) {
        return
      }
      await updateFolder({ description: folder.description }, folder.id)
    },
    async handleRejectEvent ({ actionHistory, id }) {
      await updateFolderStatus(id, { status: FOLDER_STATUS.REJECTED, actionHistory }, ActionTypes.folders.reject)
      await this.loadProject(this.projectId)
      EventBus.emit(`modules_projects_rejectModal_doClose_${this.rejectModalId}`)
    },
    rejectFolder (rejecteeId) {
      this.$refs.rejectModal.init(rejecteeId)
    },
    approveFolder (id) {
      this.$refs.approveFolder.init(id)
    },
    deleteFolder (id) {
      this.$refs.deleteFolder.init(id)
    },
    isAppr (folder) {
      return folder.status === FOLDER_STATUS.APPR
    },
    isDraft (folder) {
      return folder.status === FOLDER_STATUS.DRAFT
    },
    dblClickEdit (index, folder) {
      if (this.isPicViewLike && !this.isAppr(folder)) {
        Vue.set(this.allowChangeName, index, true)
      }
    },
    ...mapActions('projects', ['loadProject']),
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/accordion';
#project-docs-accordion {
  pre {
    display: inline-block;
    vertical-align: top;
  }
  .fa{
      margin-left: 5px;
  }
  a[id^="project-docs-accordion-"] .column {
    &:last-child {
      padding-right: 0;
    }
  }
  .tender-ellipsis-size {
    padding: 5px 10px;
    cursor: pointer;
  }

  .project-docs-accordion-menu {
    display: inline-block;
    position: absolute;
    top: -5px;
    z-index: 999;
  }

  .uppercase-bold {
    text-transform: uppercase;
    font-weight: bold;
  }

  .accordion-item {
    position: relative;
    border: 1px solid #e1e1e1;
    background-color: #f1f1f1;
  }
  .accordion-content {
    background-color: white;
    border-bottom: 1px solid #e6e6e6;
    padding: 0 30px 20px 30px;
  }
  .accordion-title {
    background-color: white;
  }

  .action-history {
    margin-top: 10px;
    border-top: 1px solid #e1e1e1;
    padding-top: 10px;
    label {
      margin-top: 15px;
      margin-bottom: 5px;
      font-size: 14px;
      line-height: 1.43;
      font-weight: bold;
      text-align: left;
      color: #0C3A58;
      text-transform: uppercase;
    }
  }

  .project-docs-accordion-info {
    padding: 0 1rem;
    background: white;
  }

  .project-docs-accordion-desc {
    padding: 15px 20px;
  }

  .project-docs-accordion-approver {
    padding-left: 0;
    padding-bottom: 1rem;
    background-color: #f8f8f8;
    text-transform: capitalize;
  }

  .title-label {
    position: relative;
    padding-left: 40px;
  }

  .title-index {
    position: absolute;
    left: 10px;
  }

  .folder-title{
    vertical-align: top;
  }

  input[id^="project-docs-accordion-doctype-desc-"] {
    margin: 0;
    width: 60%;
  }

  .doc-status {
    position: relative;
  }

  .doc-status-rect {
    position: absolute;
    top: -6px;
    right: 0;
    text-align: center;
    font-size: 14px;
    padding: 5px 12px;
  }

  .accepted-status {
    color: #009588;
    border: solid 1px #009588;
  }

  .rejected-status {
    color: #f44336;
    border: solid 1px #f44336;
  }
  .draft-status {
    color: #6e7a92;
    border: solid 1px #6e7a92;
  }
}
</style>

<style lang="scss">
#project-docs-accordion {
  .edit-attachments {
    .number {
      text-align: left;
    }
    th.number {
      padding-left: 0.625rem;
    }
  }
}
</style>
